"use client"

import { useState } from "react"
import Image from "next/image"
import { ArrowLeft, ArrowRight } from "lucide-react"

interface CoreValue {
    id: string
    title: string
    image: string
    bullets: string[]
}

const coreValues: CoreValue[] = [
    {
        id: "dan-than",
        title: "Dấn thân",
        image: "/images/About/danthan.png",
        bullets: [
            "Dấn thân, trải nghiệm để mở cửa tương lai, Rovi Holdings luôn chủ động đón nhận, thay đổi và chấp nhận mọi thử thách.",
            "Để cao chủ trương doanh nghiệp tự chủ, tự học và tạo nên những giá trị đột phá, khác biệt.",
            "Không dừng lại ở việc nắm bắt cơ hội hay quyết định táo bạo trong kinh doanh, Rovi Holdings còn thể hiện sự bền bỉ, tạo tiền đề vững chắc và sẵn sàng bất đầu cho những lần dấn thân sau đó.",
        ],
    },
    {
        id: "trach-nhiem",
        title: "Trách nhiệm",
        image: "/images/About/trachnhiem.png",
        bullets: [
            `Hiểu rằng "Nhân sự là tài sản quý giá" nên Rovi Holdings luôn chú trọng xây dựng môi trường làm việc chuyên nghiệp, năng động, sáng tạo và nhân văn cho toàn bộ nhận viên.`,
            "Hiểu rõ sứ mệnh phục vụ khách hàng, đặt lợi ích và mong muốn của khách hàng lên hàng đầu.",
            "Xây dựng mối quan hệ với khách hàng, đối tác, nhà đầu tư và tham gia các hoạt động xã hội bằng chữ tín, sự thiện chí, chuyên nghiệp và nhân văn.",
        ],
    },
    {
        id: "hieu-qua",
        title: "Hiệu quả",
        image: "/images/About/hieuqua.png",
        bullets: [
            "Chiêu mộ những nhân sự tinh hoa, vừa có tâm vừa có tầm nhằm tạo ra những sản phẩm - dịch vụ giá trị và tốt nhất.",
            `Xây dựng đội ngũ nhân sự tinh gọn luôn là mục tiêu hàng. Chúng tôi lấy tôn chỉ: "Làm đúng việc - thực hiện đúng cách" để làm giá trị bản sắc để phát triển.`,
            "Đề cao tinh thần sáng tạo, dám nghĩ dám làm của nhân viên, khuyến khích tìm tòi và mở ra môi trường để họ thỏa sức thể hiện đam mê và năng lực của mình.",
        ],
    },
]

export default function CoreValuesSection() {
    const [activeIndex, setActiveIndex] = useState(0)

    const handlePrevious = () => {
        setActiveIndex((prev) => (prev === 0 ? coreValues.length - 1 : prev - 1))
    }

    const handleNext = () => {
        setActiveIndex((prev) => (prev === coreValues.length - 1 ? 0 : prev + 1))
    }

    const commonTitleClass =
        "text-xl xl:text-xl font-normal text-[#393939] transition-colors duration-300 group-hover:text-[#006633]"

    return (
        // Section giá trị cốt lõi
        <section className="pt-8 pb-2 md:py-12 px-6 md:px-12 xl:px-20 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 xl:grid-cols-[0.7fr_1.3fr] gap-8 xl:gap-16 items-start">

                    {/* Cột trái danh sách giá trị */}
                    <div className="hidden xl:block relative pt-8 xl:pt-18 pl-6 xl:pl-12 space-y-8 xl:space-y-10">

                        {/* Đường kẻ trang trí */}
                        <div className="absolute -left-5 xl:-left-10 top-2 xl:top-8 -bottom-20 xl:-bottom-32 w-[1px] bg-gray-200" />

                        {/* Tiêu đề khu vực */}
                        <div className="relative -translate-y-2 xl:-translate-y-4 -translate-x-2 xl:-translate-x-4">
                            <p
                                className="text-xs font-normal text-[#006633] tracking-wider mb-1 uppercase"
                                style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                            >
                                Về chúng tôi
                            </p>
                            <h2
                                className="text-3xl xl:text-4xl font-medium text-[#2D3746]"
                                style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                            >
                                Giá trị cốt lõi
                            </h2>
                        </div>

                        {/* Danh sách giá trị */}
                        <div className="flex flex-col gap-6 xl:gap-8">
                            {coreValues.map((value, index) => {
                                const isActive = activeIndex === index
                                return (
                                    <div key={value.id} className="relative group cursor-pointer">

                                        {/* Chỉ báo trạng thái */}
                                        <div
                                            className={`absolute -left-[50px] xl:-left-[90px] w-1.5 h-1.5 rounded-full transition-all duration-500 ease-in-out
                                                ${isActive
                                                    ? "top-[41px] bg-red-600 scale-125"
                                                    : "top-1/2 -translate-y-1/2 bg-red-500 group-hover:scale-125"
                                                }
                                            `}
                                        />

                                        <button
                                            onClick={() => setActiveIndex(index)}
                                            className="text-left w-full outline-none"
                                        >
                                            {isActive ? (
                                                <div className="flex flex-col items-start gap-4">
                                                    {/* Ảnh trạng thái active */}
                                                    <div className="relative w-14 h-14 -ml-2 rounded-full overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                        <Image
                                                            src="/images/About/globe.gif"
                                                            alt="Spinning Globe"
                                                            width={57}
                                                            height={57}
                                                            className="object-cover scale-110"
                                                            unoptimized
                                                        />
                                                    </div>
                                                    <span
                                                        className={commonTitleClass}
                                                        style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                                                    >
                                                        {value.title}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="py-1">
                                                    <span className={commonTitleClass}>{value.title}</span>
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Cột phải nội dung chi tiết */}
                    <div className="w-full">

                        {/* Header mobile */}
                        <div className="xl:hidden text-left mb-6">
                            <p className="text-xs font-normal text-[#006633] tracking-wider mb-1 uppercase" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                                Về chúng tôi
                            </p>
                            <h2 className="text-3xl font-medium text-[#2D3746]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                                Giá trị cốt lõi
                            </h2>
                        </div>

                        {/* Khối nội dung chính */}
                        <div className="relative h-full xl:min-h-[420px] flex flex-col xl:block xl:pt-0 xl:-ml-12 xl:translate-x-8 xl:translate-y-10">

                            {/* Ảnh minh họa */}
                            <div className="relative w-full rounded-xl overflow-hidden aspect-[16/9] xl:aspect-auto xl:h-[440px]">
                                <Image
                                    src={coreValues[activeIndex].image || "/placeholder.svg"}
                                    alt={coreValues[activeIndex].title}
                                    fill
                                    className="object-cover"
                                    priority
                                />

                                {/* Nội dung trên ảnh */}
                                <div className="hidden xl:block absolute bottom-0 left-0 right-0 p-8 text-white">
                                    <div className="relative flex flex-col justify-end mb-3">
                                        <span
                                            className="relative z-0 text-7xl font-bold tracking-tighter leading-[0.8] -mb-5 select-none"
                                            style={{
                                                backgroundImage: "linear-gradient(180deg, #FFF 8.13%, rgba(243, 249, 255, 0.00) 82.07%)",
                                                WebkitBackgroundClip: "text",
                                                WebkitTextFillColor: "transparent",
                                                backgroundClip: "text",
                                                color: "transparent",
                                            }}
                                        >
                                            0{activeIndex + 1}
                                        </span>
                                        <span
                                            className="relative z-10 text-xl font-light text-white drop-shadow-lg pl-1"
                                            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                                        >
                                            {coreValues[activeIndex].title}
                                        </span>
                                    </div>
                                    <ul className="space-y-3 max-w-xl md:max-w-xl xl:max-w-xl 2xl:max-w-2xl min-[1920px]:max-w-2xl mt-4">
                                        {coreValues[activeIndex].bullets.map((bullet, idx) => (
                                            <li key={idx} className="flex items-start gap-3">
                                                <span className="h-4 flex items-center text-white flex-shrink-0 text-[10px] xl:text-[11px] 2xl:text-[12px] min-[1920px]:text-[14px] drop-shadow-lg">•</span>
                                                <span className="text-[12px] xl:text-[11px] 2xl:text-[13px] min-[1920px]:text-[16px] font-light leading-4 text-white drop-shadow-lg" style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}>{bullet}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Điều hướng slider */}
                                <div className="absolute top-1/2 -translate-y-1/2 right-2 xl:right-4 flex flex-col gap-2 z-20">
                                    <button onClick={handlePrevious} className="w-8 h-8 rounded-full border border-white/30 text-white hover:bg-white hover:text-black flex items-center justify-center transition-all duration-300">
                                        <ArrowLeft className="w-3 h-3" />
                                    </button>
                                    <button onClick={handleNext} className="w-8 h-8 rounded-full border border-white/30 text-white hover:bg-white hover:text-black flex items-center justify-center transition-all duration-300">
                                        <ArrowRight className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>

                            {/* Nội dung mobile */}
                            <div className="xl:hidden mt-6 text-left pl-1">
                                <div className="relative flex flex-col justify-end mb-4">
                                    <span
                                        className="relative z-0 text-6xl font-bold tracking-tighter leading-[0.8] -mb-5 select-none"
                                        style={{
                                            backgroundImage: "linear-gradient(180deg, #CACACA 17.93%, rgba(243, 249, 255, 0.00) 90.34%)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                            color: "transparent",
                                        }}
                                    >
                                        0{activeIndex + 1}
                                    </span>
                                    <span
                                        className="relative z-10 text-xl font-medium text-[#2D3746] pl-1"
                                        style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                                    >
                                        {coreValues[activeIndex].title}
                                    </span>
                                </div>

                                <ul className="space-y-3 pl-1">
                                    {coreValues[activeIndex].bullets.map((bullet, idx) => (
                                        <li key={idx} className="flex items-start gap-2">
                                            <span className="h-5 flex items-center text-[#2D3746] flex-shrink-0 text-[10px]">•</span>
                                            <span className="text-[13px] font-light leading-snug text-gray-600" style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}>{bullet}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </section>
    )
}
